/**
 * Pure timezone search over the server's own accept-list (D-P5-1).
 *
 * The server list (GET /api/users/settings/timezones) is the single
 * authority: it is exactly what the PUT /settings validator accepts, so this
 * module only ever RETURNS zones from that list. The curated JSON files are a
 * thin patch over it — city names and metonyms the IANA IDs don't contain
 * ("bombay", "nyc"), and ambiguous abbreviations ("IST") whose every
 * candidate must be shown for an explicit pick, never auto-resolved.
 *
 * Matching is prefix-from-the-first-character across three lanes, in result
 * order: IANA name path segments, city aliases, abbreviations. An
 * abbreviation hit is flagged with `matchedAbbreviation` so the UI can
 * annotate the row; a zone already found by name or alias keeps its direct,
 * unflagged form.
 */
import cityAliasesJson from './cityAliases.json';
import zoneAbbreviationsJson from './zoneAbbreviations.json';

/** One row of GET /api/users/settings/timezones. */
export interface TimezoneOption {
  name: string;
  offset_minutes: number;
  abbreviation: string | null;
}

export interface TimezoneMatch {
  option: TimezoneOption;
  /** Set when the row was reached through an abbreviation ("IST"); the UI
   * annotates it and the user must still pick an explicit IANA zone. */
  matchedAbbreviation?: string;
}

/** Drop the `_meta` documentation key the data files carry. */
const stripMeta = <T,>(raw: Record<string, unknown>): Record<string, T> => {
  const out: Record<string, T> = {};
  Object.keys(raw).forEach((key) => {
    if (!key.startsWith('_')) out[key] = raw[key] as T;
  });
  return out;
};

export const CITY_ALIASES: Record<string, string> = stripMeta<string>(
  cityAliasesJson as unknown as Record<string, unknown>,
);

export const ZONE_ABBREVIATIONS: Record<string, string[]> = stripMeta<string[]>(
  zoneAbbreviationsJson as unknown as Record<string, unknown>,
);

/** Lowercase, strip diacritics, treat underscores as spaces, collapse runs of
 * whitespace — so "SÃO_Paulo" and "sao paulo" are the same query. */
export const normalizeZoneText = (raw: string): string =>
  raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Zone names are stable strings, so their normalized forms are memoized;
 * the alias tables are normalized once at module load via their keys. */
const nameCandidatesCache = new Map<string, string[]>();

const nameCandidates = (name: string): string[] => {
  let candidates = nameCandidatesCache.get(name);
  if (!candidates) {
    candidates = [normalizeZoneText(name), ...name.split('/').map(normalizeZoneText)];
    nameCandidatesCache.set(name, candidates);
  }
  return candidates;
};

/**
 * Match `query` against the server's zone list. Matching starts from the
 * first character; an empty query returns the whole list unflagged. Aliases
 * or abbreviation candidates naming a zone the server lacks are dropped —
 * the result can never contain a value the server would 422.
 */
export const searchTimezones = (
  query: string,
  serverZones: TimezoneOption[],
): TimezoneMatch[] => {
  const q = normalizeZoneText(query);
  if (!q) return serverZones.map((option) => ({ option }));

  const byName = new Map(serverZones.map((option) => [option.name, option]));
  const seen = new Set<string>();
  const results: TimezoneMatch[] = [];

  // Lane 1: the IANA name itself — full path or any path segment.
  serverZones.forEach((option) => {
    if (nameCandidates(option.name).some((candidate) => candidate.startsWith(q))) {
      results.push({ option });
      seen.add(option.name);
    }
  });

  // Lane 2: curated city aliases (keys are stored pre-normalized).
  Object.keys(CITY_ALIASES).forEach((alias) => {
    if (!alias.startsWith(q)) return;
    const zone = CITY_ALIASES[alias];
    const option = byName.get(zone);
    if (!option || seen.has(zone)) return;
    results.push({ option });
    seen.add(zone);
  });

  // Lane 3: abbreviations — every candidate surfaces, each flagged, so the
  // UI can demand an explicit pick instead of auto-resolving "IST".
  Object.keys(ZONE_ABBREVIATIONS).forEach((abbreviation) => {
    if (!abbreviation.toLowerCase().startsWith(q)) return;
    ZONE_ABBREVIATIONS[abbreviation].forEach((zone) => {
      const option = byName.get(zone);
      if (!option || seen.has(zone)) return;
      results.push({ option, matchedAbbreviation: abbreviation });
      seen.add(zone);
    });
  });

  return results;
};
